import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import InseeSearch from '@/components/InseeSearch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Edit, Trash2, Building2, Loader2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptySupplier = { nom: '', siret: '', adresse: '', code_postal: '', ville: '', pays: 'France', telephone: '', email: '', contact_nom: '', contact_email: '', iban: '', bic: '', tva_intracommunautaire: '', categorie: '', compte_comptable_default: '', notes: '' };

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySupplier);
  const [saving, setSaving] = useState(false);

  const fetchSuppliers = async () => {
    try {
      const params = { skip: 0, limit: 500 };
      if (search) params.q = search;
      if (catFilter && catFilter !== 'all') params.categorie = catFilter;
      const res = await axios.get(`${API_URL}/suppliers`, { params });
      setSuppliers(res.data);
    } catch (e) { toast.error('Erreur chargement fournisseurs'); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/suppliers/categories`);
      setCategories(res.data.categories || []);
    } catch (e) {}
  };

  useEffect(() => { fetchSuppliers(); fetchCategories(); }, [search, catFilter]);

  const handleSave = async () => {
    if (!form.nom) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/suppliers/${editing}`, form);
        toast.success('Fournisseur modifié');
      } else {
        await axios.post(`${API_URL}/suppliers`, form);
        toast.success('Fournisseur créé');
      }
      setDialogOpen(false); setEditing(null); setForm(emptySupplier);
      fetchSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    try {
      await axios.delete(`${API_URL}/suppliers/${id}`);
      toast.success('Fournisseur désactivé');
      fetchSuppliers();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const openEdit = (s) => { setEditing(s.id); setForm({ nom: s.nom, siret: s.siret || '', adresse: s.adresse || '', code_postal: s.code_postal || '', ville: s.ville || '', pays: s.pays || 'France', telephone: s.telephone || '', email: s.email || '', contact_nom: s.contact_nom || '', contact_email: s.contact_email || '', iban: s.iban || '', bic: s.bic || '', tva_intracommunautaire: s.tva_intracommunautaire || '', categorie: s.categorie || '', compte_comptable_default: s.compte_comptable_default || '', notes: s.notes || '' }); setDialogOpen(true); };

  const [comptesCharges, setComptesCharges] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/comptabilite/comptes`, { params: { classe: '6' } })
      .then(res => setComptesCharges(res.data || []))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Fournisseurs</h1>
          <p className="text-slate-500">{suppliers.length} fournisseur(s)</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptySupplier); setDialogOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]">
          <Plus className="w-4 h-4 mr-2" /> Nouveau fournisseur
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher par nom, SIRET..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {categories.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead><TableHead>Nom</TableHead><TableHead>SIRET</TableHead>
                  <TableHead>Catégorie</TableHead><TableHead>Email</TableHead><TableHead>Factures</TableHead>
                  <TableHead>Total HT</TableHead><TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map(s => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-sm">{s.code}</TableCell>
                    <TableCell className="font-medium">{s.nom}</TableCell>
                    <TableCell>{s.siret || '-'}</TableCell>
                    <TableCell>{s.categorie ? <Badge variant="outline">{s.categorie}</Badge> : '-'}</TableCell>
                    <TableCell>{s.email || '-'}</TableCell>
                    <TableCell>{s.invoice_count || 0}</TableCell>
                    <TableCell>{formatCurrency(s.total_ht || 0)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {suppliers.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Aucun fournisseur</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</DialogTitle></DialogHeader>
          {!editing && (
            <div className="mb-2">
              <Label className="text-sm text-slate-500">Rechercher dans le répertoire INSEE</Label>
              <InseeSearch onSelect={(company) => setForm(prev => ({
                ...prev,
                nom: company.nom_complet || company.nom || prev.nom,
                siret: company.siret || prev.siret,
                adresse: company.adresse || prev.adresse,
                code_postal: company.code_postal || prev.code_postal,
                ville: company.ville || prev.ville,
                tva_intracommunautaire: company.tva_intracommunautaire || prev.tva_intracommunautaire,
              }))} placeholder="Nom ou SIREN du fournisseur..." />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Nom *</Label><Input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} /></div>
            <div><Label>SIRET</Label><Input value={form.siret} onChange={e => setForm({...form, siret: e.target.value})} /></div>
            <div><Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={v => setForm({...form, categorie: v})}>
                <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Adresse</Label><Input value={form.adresse} onChange={e => setForm({...form, adresse: e.target.value})} /></div>
            <div><Label>Code postal</Label><Input value={form.code_postal} onChange={e => setForm({...form, code_postal: e.target.value})} /></div>
            <div><Label>Ville</Label><Input value={form.ville} onChange={e => setForm({...form, ville: e.target.value})} /></div>
            <div><Label>Telephone</Label><Input value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            <div><Label>Contact nom</Label><Input value={form.contact_nom} onChange={e => setForm({...form, contact_nom: e.target.value})} /></div>
            <div><Label>Contact email</Label><Input value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} /></div>
            <div><Label>IBAN</Label><Input value={form.iban} onChange={e => setForm({...form, iban: e.target.value})} /></div>
            <div><Label>BIC</Label><Input value={form.bic} onChange={e => setForm({...form, bic: e.target.value})} /></div>
            <div><Label>TVA intracommunautaire</Label><Input value={form.tva_intracommunautaire} onChange={e => setForm({...form, tva_intracommunautaire: e.target.value})} /></div>
            <div className="col-span-2"><Label>Compte de charge par défaut (Plan Comptable)</Label>
              <Select value={form.compte_comptable_default} onValueChange={v => setForm({...form, compte_comptable_default: v})}>
                <SelectTrigger><SelectValue placeholder={comptesCharges.length ? "Choisir un compte (classe 6)" : "Aucun compte de classe 6 dans le plan comptable"} /></SelectTrigger>
                <SelectContent>{comptesCharges.map(c => <SelectItem key={c.numero} value={c.numero}>{c.numero} — {c.libelle}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Pré-rempli automatiquement à la création des factures de ce fournisseur.</p>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
